import { getStreamChatBusinessTeamName } from '@utils/streamChatUtils';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useState } from 'react';
import type { Channel, StreamChat } from 'stream-chat';
import { useStreamChat } from '~/hooks/useStreamChat';
import { useUserContext } from './UserContext';

type ChatOverlay = 'ADD_USERS_TO_CHANNEL' | 'CREATE_CHANNEL' | 'CREATE_DIRECT_MESSAGE' | 'EDIT_CHANNEL';

type CreateChannelParams = {
	channelType: ChannelType;
	members?: string[];
	name?: string;
};

type StreamChatState = {
	businessTeamName: string;
	chatOverlay: ChatOverlay | null;
	channelListChange: number;
	closeChatOverlay: () => void;
	createChannel: (params: CreateChannelParams) => Promise<Channel>;
	onAddChannelClick: (type: ChannelType) => void;
	onAddUsersToChannelClick: () => void;
	onEditChannelClick: () => void;
	onChannelListChange: () => void;
	streamChatClient: StreamChat | null;
};

export const StreamChatContext = createContext<StreamChatState | null>(null);

export const useStreamChatContext = () => {
	const streamChatContext = useContext(StreamChatContext);

	if (!streamChatContext) {
		throw new Error('Stream Chat Context Data Error');
	}

	return streamChatContext;
};

export const StreamChatProvider = (props: PropsWithChildren) => {
	const { client } = useStreamChat();
	const { businessId } = useUserContext();
	const [chatOverlay, setChatOverlay] = useState<ChatOverlay | null>(null);
	const [channelListChange, setChannelListChange] = useState(0);
	const businessTeamName = getStreamChatBusinessTeamName(businessId);

	const createChannel = async (params: CreateChannelParams) => {
		const { channelType, members, name } = params;

		if (!client) {
			throw new Error('Stream Chat Client not initialized');
		}

		const newChannel = client.channel(channelType, name, {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			members: [client.userID!, ...(members || [])],
			name,
			team: businessTeamName,
		});

		await newChannel.watch();

		return newChannel;
	};

	const onAddChannelClick = (type: ChannelType) => {
		setChatOverlay(type === 'team' ? 'CREATE_CHANNEL' : 'CREATE_DIRECT_MESSAGE');
	};

	const onAddUsersToChannelClick = () => {
		setChatOverlay('ADD_USERS_TO_CHANNEL');
	};

	const onEditChannelClick = () => {
		setChatOverlay('EDIT_CHANNEL');
	};

	const closeChatOverlay = () => {
		setChatOverlay(null);
	};

	const streamChatState: StreamChatState = {
		businessTeamName,
		chatOverlay,
		channelListChange,
		closeChatOverlay,
		createChannel,
		onAddChannelClick,
		onAddUsersToChannelClick,
		onEditChannelClick,
		onChannelListChange: () => setChannelListChange((prev) => prev + 1),
		streamChatClient: client,
	};

	return <StreamChatContext.Provider value={streamChatState}>{props.children}</StreamChatContext.Provider>;
};
